<template>
    <form
        ref="form"
        @submit.prevent="submit"
        :action="formRoute"
        method="POST"
        class="relative h-full"
    >
        <page-header
            :submit-button-text="data ? 'Update' : 'Create Pros/Cons'"
            :show-submit-button="true"
            :submit-button-disabled="submitButtonDisabled || !editable"
            submit-button-type="submit"

            :can-delete="canDelete"
            :delete-route="deleteRoute"
            :delete-value="deleteValue"

            :can-create="canCreate"
            :create-route="createRoute"
            :create-value="createValue"

            :index-route="indexRoute"
            :index-value="indexValue"
            :title="title"
            :subtitle="subtitle"

            :show-preview-button="false"

            :errors="errors"
            :is-slide-over="isSlideOver"

            :version-id="versionId"
            :init-version-id="initVersionId"
            :last-updated-by="lastUpdatedBy"
        />

        <input-rich-text
            name="editor_note"
            label="Editor's note"
            v-model="form.editor_note"
            :language="form.language"
            :error="errors.editor_note ? errors.editor_note[0] : null"
            :maxlength="60000"
        />
        <p class="my-1 text-xs text-gray-600 mb-3">The note is for internal use only and won’t be displayed on the site</p>

        <input-text
            name="name"
            label="* Name (This will not appear on the site)"
            v-model="form.name"
            :error="errors.name ? errors.name[0] : null"
            :maxlength="255"
        />

        <Select
            label="* Type"
            class="mt-3"
            name="type"
            v-model="form.type"
            :options="[
                { value: 'basic', name: 'Basic' },
                { value: 'mixed', name: 'Mixed ( Side by side)' },
                { value: 'image', name: 'With Image ( Simple List With Images style )' },
            ]"
            :error="errors.type ? errors.type[0] : null"
            @input="getColumns(form.type)"
        />

        <Select
            v-if="form.type != 'image'"
            label="* Heading Type"
            class="mt-3"
            name="heading_type"
            v-model="form.heading_type"
            :options="[
                { value: 'H2', name: 'H2' },
                { value: 'H3', name: 'H3' },
                { value: 'H4', name: 'H4' },
            ]"
            :error="errors.heading_type ? errors.heading_type[0] : null"
        />

        <input-text
            v-if="form.type == 'image'"
            label="Number of visible items"
            name="number_of_visible_items"
            v-model="form.number_of_visible_items"
            class="mt-3"
            :error="errors.number_of_visible_items ? errors.number_of_visible_items[0] : null"
        />

        <draggable
            v-model="form.prosAndConsListColumns"
            handle=".handle"
        >
            <div
                class="border bg-blue-100 rounded p-4 mt-3"
                v-for="(datum, index) in form.prosAndConsListColumns"
                :key="`prosAndConsListColumns-${index}`"
            >
                <span class="ml=5 handle" v-if="form.prosAndConsListColumns.length > 1 && form.type != 'basic'">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 17l-4 4m0 0l-4-4m4 4V3" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                    </svg>
                </span>

                <button @click="deleteRow(index)" v-if="form.prosAndConsListColumns.length > 1 && form.type == 'image'" type="button" class="-translate-y-1/2 hover:bg-red-500 hover:text-red-100 transition duration-75 bg-red-600 text-white rounded shadow float-right -mt-3">
                    <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <input-text
                    v-if="form.type == 'mixed'"
                    name="title"
                    label="* Title"
                    v-model="datum.title"
                    class="mt-3"
                    :error="errors[`prosAndConsListColumns.${index}.title`] ? errors[`prosAndConsListColumns.${index}.title`][0] : null"
                    :maxlength="255"
                />

                <media-reference
                    v-if="form.type == 'image'"
                    :language="form.language"
                    v-model="datum.image"
                    class="mt-3"
                    label="* Media"
                    @input="updateImage(index, $event)"
                    :error="errors[`prosAndConsListColumns.${index}.image_id`] ? errors[`prosAndConsListColumns.${index}.image_id`][0] : null"
                />

                <div
                    v-for="(proAndCons, proAndConsIndex) in datum"
                    :key="`prosAndCons-${proAndConsIndex}`"
                >
                    <pros-and-cons-input
                        v-if="proAndConsIndex == 'pros'"
                        :button-text="form.type != 'image' ? '+ Add Pro' : ''"
                        :error-bag="errors"
                        :key-index="`prosAndConsListColumns.${index}`"
                        v-model="datum.pros"
                        title="* Pros"
                        name="pros"
                        :maxlength="255"
                    />

                    <pros-and-cons-input
                        v-if="proAndConsIndex == 'cons'"
                        :button-text="form.type != 'image' ? '+ Add Con' : ''"
                        :error-bag="errors"
                        :key-index="`prosAndConsListColumns.${index}`"
                        v-model="datum.cons"
                        title="* Cons"
                        name="cons"
                        :maxlength="255"
                    />
                </div>
            </div>
        </draggable>
        <Btn
            v-if="form.type == 'image'"
            @click="addRow('image')"
            class="mt-3"
            type="button"
            content="+ Add New Row"
        />
        <input type="hidden" name="_token" :value="csrf" />
        <input v-if="data" type="hidden" name="_method" value="patch" />
    </form>
</template>

<script>
import crudForm from "../mixins/crudForm";
import draggable from "vuedraggable";
import {v4 as uuidv4} from "uuid";

export default {
    components: { draggable },
    name: "ProsAndConsForm",
    mixins: [crudForm],
    data() {
        return {
            form: {
                editor_note: this.getFormData("editor_note"),
                name: this.getFormData("name"),
                type: this.getFormData("type", 'basic'),
                heading_type: this.getFormData("heading_type", 'H4'),
                number_of_visible_items: this.getFormData("number_of_visible_items"),
                image_id: this.getFormData("image_id"),
                image: this.getFormData("image"),
                prosAndConsListColumns: this.getFormData("pros_and_cons_list_columns", []),
            },
        };
    },
    created() {
        if (!this.form.prosAndConsListColumns.length) {
            this.getColumns(this.form.type);
        } else if (this.form.type == 'basic') {
            this.form.prosAndConsListColumns[0] = {
                'pros': this.form.prosAndConsListColumns[0]['pros_and_cons_column_items'],
                'cons': this.form.prosAndConsListColumns[1]['pros_and_cons_column_items'],
            };
            this.form.prosAndConsListColumns.splice(1, 1);
        } else {
            let prosAndCons = [];
            for (let i = 0; i < this.form.prosAndConsListColumns.length; i++) {
                if (this.form.type == 'mixed') {
                    prosAndCons[i] = {
                        'title': this.form.prosAndConsListColumns[i].title,
                    };
                } else if (this.form.type == 'image') {
                    prosAndCons[i] = {
                        'image': this.form.prosAndConsListColumns[i].image,
                        'image_id': this.form.prosAndConsListColumns[i].image_id,
                    };
                }
                prosAndCons[i]['pros'] = [];
                prosAndCons[i]['cons'] = [];
                for (let j = 0; j < this.form.prosAndConsListColumns[i]['pros_and_cons_column_items'].length; j++) {
                    prosAndCons[i][`${this.form.prosAndConsListColumns[i]['pros_and_cons_column_items'][j].type}s`].push({
                        'text': this.form.prosAndConsListColumns[i]['pros_and_cons_column_items'][j]['text'],
                        'id': this.form.prosAndConsListColumns[i]['pros_and_cons_column_items'][j]['id'],
                    });
                }
            }
            this.form.prosAndConsListColumns = prosAndCons;
        }
    },
    methods: {
        getColumns(type) {
            this.form.number_of_visible_items = null;
            if (type == 'basic') {
                this.form.heading_type = this.form.heading_type || 'H4';
                this.form.prosAndConsListColumns = [this.defaultRow(type)];
            } else if (type == 'mixed') {
                this.form.heading_type = this.form.heading_type || 'H4';
                this.form.prosAndConsListColumns = [
                    this.defaultRow(type),
                    this.defaultRow(type),
                ];
            } else if (type == 'image') {
                this.form.heading_type = null;
                this.form.prosAndConsListColumns = [this.defaultRow(type)];
            }
        },
        updateImage: function (itemIndex, image) {
            this.$set(this.form.prosAndConsListColumns, itemIndex, Object.assign(this.form.prosAndConsListColumns[itemIndex], {image_id: (image && image.id) ? image.id : null}));
        },
        addRow(type) {
            this.form.prosAndConsListColumns.push(this.defaultRow(type));
        },
        deleteRow: function (index) {
            if (confirm('Are you sure you want to remove this row?')) {
                this.form.prosAndConsListColumns.splice(index, 1);
            }
        },
        defaultRow(type) {
            if (type == 'image') {
                return {
                    'image' : null,
                    'pros': [{
                        'text': null,
                        'uuid': uuidv4(),
                    }],
                    'cons': [{
                        'text' : null,
                        'uuid': uuidv4(),
                    }],
                };
            } else if (type == 'mixed') {
                return {
                    'title' : null,
                    'pros': [{
                        'text': null,
                        'uuid': uuidv4(),
                    }],
                    'cons': [{
                        'text' : null,
                        'uuid': uuidv4(),
                    }],
                };
            }
            return {
                'pros': [{
                    'text': null,
                    'uuid': uuidv4(),
                }],
                'cons': [{
                    'text' : null,
                    'uuid': uuidv4(),
                }],
            };
        },
    },
};
</script>
